import {MenuNavigation, isVisible} from '../../static/menuNavigation.js';
import '../../static/menuNavigation.css';
import {input} from '../input/InputManager.js';
import {localize} from '../utils/Localization';

export const menuNavigation = new MenuNavigation({
    labels: {
        get cancel() { return localize('controls_cancel'); },
        get chooseOption() { return localize('controls_choose_option'); },
    },
    onChange: () => input.reset(),
    onExit: () => {
        document.activeElement?.blur();
        document.querySelector('canvas')?.focus({preventScroll: true});
    },
});

const click = (root, selector) => {
    const target = [...root.querySelectorAll(selector)].find(element => isVisible(element));
    if (target) target.click();
};

export function configureMenuNavigation(ui, getGame, isMobile) {
    menuNavigation.onInteraction = () => getGame()?.gameplaySession.interact();
    menuNavigation.enabled = () => {
        const game = getGame();
        return !isMobile() && game && game.gameState !== game.GAMESTATE_EDITOR &&
            document.getElementById('preloader')?.classList.contains('hide');
    };
    const decorate = element => {
        const checkbox = element.matches('[data-nav-group-control]') && element.querySelector('input');
        if (checkbox) {
            element.setAttribute('role', element.classList.contains('feature-toggle') ? 'switch' : 'radio');
            element.setAttribute('aria-checked', String(checkbox.checked));
        }
        const labels = {settings: 'Settings', audio: 'Toggle sound', 'copy-button': 'Copy link', close: 'Close',
            'vote-up': 'Like level', 'vote-down': 'Dislike level', 'search-icon': 'Search levels', discordButton: 'Join Discord',
            comments: 'Comments'};
        if (!element.getAttribute('aria-label')) {
            const name = Object.keys(labels).find(name => element.classList.contains(name));
            if (name) element.setAttribute('aria-label', labels[name]);
        }
    };
    const register = (id, selector, options) => menuNavigation.register(id, selector, {decorate, ...options});
    const retryShortcut = event => {
        if (input.blocked) return false;
        const action = input.match('game', event);
        return ['retry', 'restart'].includes(action) && getGame().handleInputAction(action, event);
    };
    const mainItems = '.settings, .audio, .menu-grid > div, .discord-strip button';
    register('main', '.mainmenu', {selector: mainItems, defaultFocus: '.singleplayer-but',
        when: root => isVisible(root.querySelector('.menu-grid')), exclude: '.editor-but.mobile'});
    register('multiplayer', '.mainmenu', {selector: '.settings, .audio, .update-reload, .quick-play-button, .create-lobby-button, .code-input, .join-code-button, .refresh-button, .lobby-entry, .lobby-browser .back-button',
        defaultFocus: '.quick-play-button',
        when: root => isVisible(root.querySelector('.multiplayer-browser')), back: root => click(root, '.lobby-browser .back-button')});
    register('lobby', '.mainmenu', {selector: '.settings, .audio, .lobby-code, .lobby-edit, .selectBut, .copy-button, .kick-but, .leave-but, .ready-but',
        defaultFocus: '.ready-but:not([aria-disabled=true]), .selectBut', when: root => isVisible(root.querySelector('.multiplayer-lobby')),
        back: root => click(root, '.leave-but')});
    register('levels', '.singleplayer', {selector: '.filters, .search-icon, .exit, .creators > .creator, .games > .game:not(.game-template)',
        exclude: '.filters-fold *', defaultFocus: '.games > .game:not(.game-template)', back: root => click(root, '.header .exit')});
    register('filters', '.singleplayer .filters-container.open', {priority: 20,
        selector: '.filters, .feature-toggle, .checkbox-container, .date > .button, .vehicles > .button',
        exclude: 'input', defaultFocus: '.feature-toggle', back: root => click(root, '.filters')});
    for (const name of ['date', 'vehicles']) register(`filter-${name}`, `.singleplayer .filters-container.open .${name}.open`, {
        priority: 30, selector: '.button', defaultFocus: '.checked', back: root => click(root, '.clicked, .checked')});
    const social = '.share, .save, .comments, .vote-up, .vote-down, .text-author, .text-player-name, .viewall, .video:not(.video-template)';
    register('level', '.levelbanner', {priority: 100, selector: `.back, .play, ${social}`, defaultFocus: '.play', back: root => click(root, '.back')});
    register('profile', '.userPage', {priority: 100, selector: '.rank-discord button, .game:not(.game-template)', defaultFocus: '.togglebuttons .games', back: () => ui.hideUserPage()});
    register('leaderboard', '.leaderboard', {priority: 100, selector: '.back, .text-player-name', defaultFocus: '.back', back: () => ui.hideLeaderboard()});
    register('comments', '.levelcomments', {priority: 100, selector: '.comment-input, .post, .comment-login, .comment-delete, .more, .back',
        defaultFocus: '.back', back: () => ui.hideLevelComments()});
    register('settings', '.settingsmenu:not(.controls-panel)', {priority: 100, defaultFocus: '.controls', back: () => ui.hideSettingsMenu()});
    register('controls', '.controls-panel', {priority: 110, defaultFocus: '[data-device=keyboard]', back: root => click(root, '.back')});
    register('characters', '.characterSelect', {priority: 100, back: () => ui.hideCharacterSelect()});
    register('vehicles', '.vehicleselect', {priority: 100, selector: '.portrait, .back', defaultFocus: '.portrait', back: root => click(root, '.back')});
    register('pause', '.pausescreen', {priority: 50, selector: '.buttons > div, .share, .save, .vote-up, .vote-down', defaultFocus: '.resume',
        back: root => click(root, '.resume'), pause: root => click(root, '.resume'), shortcut: retryShortcut});
    for (const name of ['gameover', 'winscreen']) register(name, `.${name}`, {priority: 50,
        selector: '.buttons > div, .vote-up, .vote-down, .recommendations > .game', defaultFocus: '.retry',
        when: () => !ui.endScreenInputLocked(), back: root => click(root, '.exit'), shortcut: retryShortcut});
    register('share', '.socialshare', {priority: 100, selector: '.copy-button, .close', defaultFocus: '.copy-button', back: () => ui.hideSocialShareMenu()});
    register('video', '.youtubeplayer', {priority: 100, selector: '.close, .subscribe-button', defaultFocus: '.close', back: () => ui.hideYouTubePlayer()});
    register('multiplayer-dialog', '.mp-dialog-backdrop', {priority: 200, selector: '.dialog-input, .shuffle-name, .visibility-option, .stepper-button, .dialog-button',
        defaultFocus: '.dialog-input, .dialog-button.confirm', back: root => click(root, '.dialog-button.cancel, .dialog-button.confirm')});
    register('login', '.loginscreen', {priority: 150, selector: '.back, .discord', defaultFocus: '.discord', back: root => click(root, '.back')});
    register('discord', '.discordJoin', {priority: 100, selector: '.join, .open, .back', defaultFocus: '.join, .open', back: () => ui.hideDiscordJoin()});
}
